import {
  LogOut as Logout,
  Menu as MenuIcon,
  Settings,
  ShoppingCart as ShoppingCartTwoTone,
  UserCircle as PersonTwoTone,
} from "lucide-react";
import { Badge, Menu, MenuItem } from "../../../../components/ui/MenuPrimitives";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { KRUMAGO } from "../../../s3-assets/Krumago";
import { useCart } from "../../../../context/CartContext";
import { getAllProducts } from "../../../../api/action/products";
import { fetchCompanyDetails } from "../../../../api/action/company";
import { logout } from "../../../../api/action/user";
import { useTemplateAuthState } from "../../../../template/TemplateAuthState";

const TealFlowTemplateHeader = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const nav = useNavigate();
  const dispatch = useDispatch();
  const { isLoggedIn: isAuthenticated } = useTemplateAuthState();
  const [logo, setLogo] = useState("");
  const companyPrimayId = useSelector((state) => state.companyReducer.company?.primaryId);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { total_items } = useCart();
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchProducts = async (retries = 3, delay = 1000) => {
      try {
        if (!companyPrimayId) {
          setCategoryProducts([]);
          setCategories([]);
          return;
        }

        setLoading(true);
        const response = await getAllProducts(companyPrimayId)(dispatch);

        if (response?.length > 0) {
          setCategoryProducts(response);
          const uniqueCategories = [
            ...new Set(response.map((product) => product.category.name)),
          ];
          setCategories(uniqueCategories);
        }
      } catch {
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          fetchProducts(retries - 1, delay);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [companyPrimayId, dispatch]);

  useEffect(() => {
    const fullUrl = window.location.href;
    const subdomain = fullUrl.split("/").slice(0, 3).join("/");
    fetchCompanyDetails(subdomain)
      .then((response) => {
        if (
          response.data &&
          response.data.logoLocation.includes("/MicrosoftTeams-image.png")
        ) {
          setLogo(KRUMAGO.IMAGES.AVPS);
        } else {
          setLogo(response.data.logoLocation);
        }
      })
      .catch(() => {
        setLogo(KRUMAGO.IMAGES.KRUMAGO_IMG);
      });
  }, []);

  const handleNavigation = (e, value) => {
    if (value === "Logout") {
      void dispatch(logout({ reason: "template-header-logout" })).finally(() => {
        nav("/sign-in", { replace: true });
      });
    } else {
      nav(value);
    }
  };

  const handleCart = () => {
    if (!isAuthenticated) {
      nav("/sign-in");
      return;
    }
    nav("/cart");
  };

  const menuItems = [
    { name: "Home", href: "/" },
    ...categories.slice(0, 5).map((category) => ({
      name: category,
      href: `/category/tf/${category}`,
    })),
  ];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowResults(e.target.value.length > 0);
  };

  const filteredProducts = categoryProducts.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const clearSearch = () => {
    setSearchQuery("");
    setShowResults(false);
  };

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  useEffect(() => {
    const handleError = localStorage.getItem("handleError");
    if (handleError === "true") {
      setIsHeaderVisible(false);
    } else {
      setIsHeaderVisible(true);
    }

    const handleStorageChange = (event) => {
      if (event.key === "handleError") {
        setIsHeaderVisible(event.newValue !== "true");
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  if (!isHeaderVisible) {
    return null;
  }

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  return (
    <>
      {localStorage.getItem("domainType") === "RETAIL" && (
        <div className="header sticky top-0 safe-area-top w-full bg-white shadow-md z-50 transition-shadow duration-300 ease-in-out">
          {loading && (
            <div className="flex items-center justify-center min-h-screen">
              {/* <Loader /> */}
            </div>
          )}
          <div className="mx-auto h-20 flex items-center justify-between px-2 sm:px-4 lg:px-6 max-w-12xl">
            <div className="flex items-center space-x-2">
              <Link to="/" title="AVPS Technologies Inc.">
                <img
                  loading="lazy"
                  src={logo}
                  alt="Company Logo"
                  className="h-12"
                />
              </Link>

              {/* Hamburger menu icon for mobile view */}
              <div className="lg:hidden">
                <MenuIcon
                  onClick={toggleMenu}
                  className="text-gray-700 cursor-pointer"
                />
              </div>

              {/* Menu items for larger screens */}
              <div className="hidden lg:flex flex-grow items-center justify-center space-x-5">
                {menuItems.map((item) => (
                  <div
                    key={item.name}
                    className="menu-item hover:underline transition duration-300"
                    style={{ fontSize: "18px" }}
                  >
                    <Link
                      to={item.href}
                      className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition duration-200"
                    >
                      {item.name || ""}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            {/* Search and Profile */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="search-bar border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-300"
                  placeholder="Search products..."
                />
                {showResults && (
                  <div className="absolute z-10 bg-white border border-gray-300 rounded-md w-full mt-1 max-h-60 overflow-auto">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={clearSearch}
                      >
                        {product.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Badge
                badgeContent={total_items}
                color="primary"
                onClick={handleCart}
              >
                <ShoppingCartTwoTone className="text-teal-600" />
              </Badge>
              <div onClick={handleProfileClick} className="relative">
                <PersonTwoTone className="text-teal-600 cursor-pointer" />
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={(e) => handleNavigation(e, "Profile")}>
                    <PersonTwoTone className="mr-2" />
                    Profile
                  </MenuItem>
                  <MenuItem onClick={(e) => handleNavigation(e, "Settings")}>
                    <Settings className="mr-2" />
                    Settings
                  </MenuItem>
                  <MenuItem onClick={(e) => handleNavigation(e, "Logout")}>
                    <Logout className="mr-2" />
                    Logout
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="flex flex-col lg:hidden bg-white shadow-md">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="menu-item text-gray-700 hover:text-teal-600 px-4 py-2"
                  onClick={toggleMenu}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TealFlowTemplateHeader;
